/**
 * Canonical catalog of every backend endpoint the frontend consumes.
 *
 * Purpose: allow future UI refactors to reference constants instead of
 * hard-coded URL strings scattered across `App.js`. The backend contract
 * is UNCHANGED — this file simply mirrors the routes already registered in
 * `/app/backend/server.py`.
 *
 * Usage:
 *   import { endpoints, apiUrl } from './api/endpoints';
 *   axios.get(apiUrl(endpoints.components.list));
 *   axios.put(apiUrl(endpoints.components.byId(id)));
 *
 * IMPORTANT: adding a new endpoint here does NOT create a backend route.
 * Only mirror routes that already exist in `server.py`. If a redesign
 * needs a new backend route, ask the human first — DO NOT invent one.
 */

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const apiUrl = (path) => `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

export const endpoints = {
  auth: {
    register: '/auth/register',
    login: '/auth/login',
    changePassword: '/auth/change-password',
    resetPassword: '/auth/reset-password',
    me: '/auth/me',
    studentCategories: '/auth/student-categories',
    studentCategoriesFromDb: '/student-categories-from-db',
  },

  users: {
    bulkUploadTemplate: '/users/bulk-upload-template',
    bulkUploadInstructionsPdf: '/users/bulk-upload-instructions-pdf',
    bulkUploadStudents: '/users/bulk-upload-students',
    superadminList: '/superadmin/users',
    adminList: '/admin/users',
    superadminById: (id) => `/superadmin/users/${id}`,
    superadminBlock: (id) => `/superadmin/users/${id}/block`,
    superadminBulkDeleteStudents: '/superadmin/users/students/bulk-delete',
  },

  brands: {
    list: '/brands',
    create: '/brands',
  },

  components: {
    list: '/components',
    create: '/components',
    byId: (id) => `/components/${id}`,
    update: (id) => `/components/${id}`,
    delete: (id) => `/components/${id}`,
    lowStock: '/components/low-stock',
    bulkUploadTemplate: '/components/bulk-upload-template',
    bulkUploadOlderTemplate: '/components/bulk-upload-older-stock-template',
    bulkUpload: '/components/bulk-upload',
    bulkUploadOlderStock: '/components/bulk-upload-older-stock',
    masterReset: '/components/master-reset',
    updateQuantity: (id) => `/components/${id}/quantity`,
    addOlderStock: (id) => `/components/${id}/add-older-stock`,
    promoteOlderToReissuable: (id) => `/components/${id}/promote-older-to-reissuable`,
    addReusable: (id) => `/components/${id}/add-reusable`,
    superadminBulkDelete: '/superadmin/components/bulk-delete',
    recentReturnPhotos: (id) => `/components/${id}/recent-return-photos`,
  },

  // SuperAdmin second-factor
  otp: {
    request: '/superadmin/otp/request',
  },

  olderStockReturns: {
    submit: '/older-stock-returns',
    list: '/older-stock-returns',
    verify: (id) => `/older-stock-returns/${id}/verify`,
  },

  orders: {
    create: '/orders',
    list: '/orders',
    cancel: (id) => `/orders/${id}/cancel`,
    statusUpdate: (id) => `/orders/${id}/status`,
    issue: (id) => `/orders/${id}/issue`,
    clerkReject: (id) => `/orders/${id}/clerk-reject`,
    pendingForMe: '/orders/pending-for-me',
    approvalHistory: '/orders/approval-history',
    overdueReturnable: '/orders/overdue-returnable',
    overdueReturnableMine: '/orders/overdue-returnable?mine=true',
    extendReturnDue: (orderId, componentId) => `/orders/${orderId}/items/${componentId}/extend-return`,
    componentsInUsage: '/orders/components-in-usage',
    facultyList: '/admins/faculty-list',
    superadminApprovalHistory: '/superadmin/approval-history',
    superadminPending: '/superadmin/pending-approvals',
    superadminReassignFaculty: (id) => `/superadmin/orders/${id}/reassign-faculty`,
    superadminApprove: (id) => `/superadmin/orders/${id}/approve`,
  },

  returns: {
    create: '/returns',
    list: '/returns',
    statusUpdate: (id) => `/returns/${id}/status`,
    myReturnables: '/students/my-returnables',
    clerkPendingList: '/clerk/returns',
    clerkVerify: '/clerk/verify-return',
    clerkPendingIssuance: '/clerk/pending-issuance',
  },

  waitlist: {
    list: '/waitlist',
    delete: (id) => `/waitlist/${id}`,
  },

  uploads: {
    patientPhoto: '/upload/patient-photo',
    returnPhotos: '/upload/return-photos',
    clerkVerification: '/upload/clerk-verification',
    photo: '/uploads/photo',
    serve: (folder, filename) => `/uploads/${folder}/${filename}`,
  },

  reports: {
    stockPdf: '/reports/stock-pdf',
    olderStockPdf: '/reports/older-stock-pdf',
    overdueDefaultersJson: '/reports/overdue-defaulters',
    overdueDefaultersPdf: '/reports/overdue-defaulters-pdf',
    overdueDefaultersExcel: '/reports/overdue-defaulters-excel',
    sendDefaulterReminders: '/admin/send-defaulter-reminders',
  },

  dashboard: {
    stats: '/dashboard/stats',
    userStatistics: '/dashboard/user-statistics',
    orderStatistics: '/dashboard/order-statistics',
    systemStatistics: '/dashboard/system-statistics',
    componentsDetails: '/dashboard/components-details',
    ordersDetails: '/dashboard/orders-details',
    pendingOrdersDetails: '/dashboard/pending-orders-details',
    pendingReturnsDetails: '/dashboard/pending-returns-details',
    superadminStats: '/superadmin/dashboard/stats',
    superadminActivities: '/superadmin/activities',
  },

  defaulters: {
    overrideCreate: '/superadmin/defaulter-override',
    overrideDelete: (studentId) => `/superadmin/defaulter-override/${studentId}`,
    overridesList: '/superadmin/defaulter-overrides',
  },

  exchanges: {
    create: '/exchanges',
    list: '/exchanges',
    receive: (id) => `/exchanges/${id}/receive`,
    cancel: (id) => `/exchanges/${id}/cancel`,
    download: '/exchanges/download',
  },
};
